import React from 'react';
import {
    Box, Stack, Typography, Avatar, Grid, Chip, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AndroidIcon from '@mui/icons-material/Android';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import ComputerIcon from '@mui/icons-material/Computer';
import LanguageIcon from '@mui/icons-material/Language';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import TimerIcon from '@mui/icons-material/Timer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { dashboardInnerCardSx } from './DashboardPanel';

const PLATFORM_ICONS = {
    ios: PhoneIphoneIcon,
    android: AndroidIcon,
    macos: LaptopMacIcon,
    windows: ComputerIcon,
    linux: ComputerIcon,
    web: LanguageIcon,
};

const PLATFORM_COLORS = {
    ios: '#007AFF',
    android: '#3DDC84',
    macos: '#8E8E93',
    windows: '#00A4EF',
    linux: '#FCC624',
    web: '#64748b',
    unknown: '#94a3b8',
};

export const formatPlatformLabel = (platform, t) => {
    if (!platform || platform === 'unknown') {
        return t('pages.employees.insights.platformUnknown');
    }
    const key = `pages.employees.insights.platforms.${platform}`;
    const label = t(key);
    return label === key ? platform.toUpperCase() : label;
};

export const formatAveragesSummary = (averages, t) => {
    if (!averages?.hasData) return null;
    const arrival = averages.avgArrivalTime || '–';
    const departure = averages.avgDepartureTime || '–';
    const work = averages.avgNetWorkStr || '–';
    const days = averages.daysWorked ?? 0;
    return t('pages.employees.insights.avgRoutine')
        .replace('{arrival}', arrival)
        .replace('{departure}', departure)
        .replace('{work}', work)
        .replace('{days}', String(days));
};

const DeviceIcon = ({ platform, size = 28 }) => {
    const Icon = PLATFORM_ICONS[platform] || DevicesOtherIcon;
    const color = PLATFORM_COLORS[platform] || PLATFORM_COLORS.unknown;
    return (
        <Avatar sx={{ width: size, height: size, bgcolor: `${color}18`, color }}>
            <Icon sx={{ fontSize: size * 0.55 }} />
        </Avatar>
    );
};

const DeviceRow = ({ device, t, detailed = false }) => {
    const platformLabel = formatPlatformLabel(device.platform, t);
    const title = device.deviceModel || platformLabel;

    return (
        <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'action.hover',
            }}
        >
            <DeviceIcon platform={device.platform} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                    {title}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                    <Chip
                        label={platformLabel}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: `${PLATFORM_COLORS[device.platform] || PLATFORM_COLORS.unknown}22`,
                            color: PLATFORM_COLORS[device.platform] || PLATFORM_COLORS.unknown,
                        }}
                    />
                    {device.appVersion && (
                        <Typography variant="caption" color="text.secondary">
                            v{device.appVersion}
                        </Typography>
                    )}
                </Stack>
                {detailed && device.systemVersion && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {t('pages.employees.insights.systemVersion')}: {device.systemVersion}
                    </Typography>
                )}
                {detailed && device.updatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('pages.employees.insights.lastDeviceUpdate')}: {device.updatedAt}
                    </Typography>
                )}
            </Box>
        </Stack>
    );
};

const AverageMini = ({ icon, label, value }) => (
    <Box
        sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: 'action.hover',
            height: '100%',
        }}
    >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            {icon}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {label}
            </Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {value ?? '–'}
        </Typography>
    </Box>
);

const normalizeInsight = (insight) => {
    if (!insight) {
        return { devices: [], hasDevices: false, averages: { hasData: false } };
    }
    if (Array.isArray(insight.devices)) {
        return insight;
    }
    if (insight.device?.hasDevice) {
        return { ...insight, devices: [insight.device], hasDevices: true };
    }
    return { ...insight, devices: [], hasDevices: false };
};

export const getLastActiveDevice = (insight) => {
    const { devices } = normalizeInsight(insight);
    return devices[0] || null;
};

export const formatDeviceLine = (device, t) => {
    if (!device) return null;
    const platformLabel = formatPlatformLabel(device.platform, t);
    const title = device.deviceModel || platformLabel;
    const parts = [title];
    if (device.deviceModel && device.platform && device.platform !== 'unknown') {
        parts.push(platformLabel);
    }
    if (device.appVersion) {
        parts.push(`v${device.appVersion}`);
    }
    return parts.join(' · ');
};

const EmployeeCardInsights = ({ insight, loading, t }) => {
    if (loading) {
        return (
            <Box sx={(theme) => ({ ...dashboardInnerCardSx(theme), mx: 3, mb: 2, py: 1.5 })}>
                <Typography variant="caption" color="text.secondary">
                    {t('pages.employees.insights.loading')}
                </Typography>
            </Box>
        );
    }

    const data = normalizeInsight(insight);
    const { averages } = data;

    return (
        <Box sx={(theme) => ({ ...dashboardInnerCardSx(theme), mx: 3, mb: 2, p: 2 })}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', display: 'block', mb: 1 }}
            >
                {t('pages.employees.insights.averagesTitle')}
            </Typography>

            {averages?.hasData ? (
                <Grid container spacing={0.75}>
                    <Grid size={6}>
                        <AverageMini
                            icon={<LoginIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label={t('pages.employees.insights.avgArrivalShort')}
                            value={averages.avgArrivalTime}
                        />
                    </Grid>
                    <Grid size={6}>
                        <AverageMini
                            icon={<LogoutIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label={t('pages.employees.insights.avgDepartureShort')}
                            value={averages.avgDepartureTime}
                        />
                    </Grid>
                    <Grid size={6}>
                        <AverageMini
                            icon={<TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label={t('pages.employees.insights.avgWorkShort')}
                            value={averages.avgNetWorkStr}
                        />
                    </Grid>
                    <Grid size={6}>
                        <AverageMini
                            icon={<CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label={t('pages.employees.insights.avgDaysShort')}
                            value={averages.daysWorked}
                        />
                    </Grid>
                </Grid>
            ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.7 }}>
                    <ScheduleIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        {t('pages.employees.insights.noAttendance')}
                    </Typography>
                </Stack>
            )}
        </Box>
    );
};

export const EmployeeInsightsDetail = ({ insight, loading, periodLabel, t }) => {
    if (loading) {
        return (
            <Box sx={(theme) => ({ ...dashboardInnerCardSx(theme), p: 2, mb: 2 })}>
                <Typography variant="body2" color="text.secondary">
                    {t('pages.employees.insights.loading')}
                </Typography>
            </Box>
        );
    }

    const data = normalizeInsight(insight);
    const lastDevice = data.devices?.[0] || null;
    if (!lastDevice && !data.averages?.hasData) return null;

    const { averages } = data;

    return (
        <Box sx={(theme) => ({ ...dashboardInnerCardSx(theme), p: 2, mb: 2 })}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('pages.employees.insights.drawerTitle')}
            </Typography>
            {periodLabel && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {periodLabel}
                </Typography>
            )}

            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    mb: 1.5,
                    bgcolor: 'transparent',
                    '&:before': { display: 'none' },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5, display: 'block', lineHeight: 1.2 }}>
                            {t('pages.employees.insights.deviceTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {lastDevice
                                ? formatDeviceLine(lastDevice, t)
                                : t('pages.employees.insights.noDevice')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    {lastDevice ? (
                        <DeviceRow
                            key={lastDevice.deviceId || `${lastDevice.platform}-${lastDevice.deviceModel}-detail`}
                            device={lastDevice}
                            t={t}
                            detailed
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {t('pages.employees.insights.noDevice')}
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>

            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                {t('pages.employees.insights.averagesTitle')}
            </Typography>
            {averages?.hasData ? (
                <Stack spacing={1} sx={{ mt: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatAveragesSummary(averages, t)}
                    </Typography>
                    <Grid container spacing={0.75}>
                        <Grid size={6}>
                            <AverageMini
                                icon={<TimerIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                                label={t('pages.employees.insights.avgWorkShort')}
                                value={averages.avgNetWorkStr}
                            />
                        </Grid>
                        <Grid size={6}>
                            <AverageMini
                                icon={<CalendarMonthIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                                label={t('pages.employees.insights.avgDaysShort')}
                                value={averages.daysWorked}
                            />
                        </Grid>
                    </Grid>
                    {averages.avgBreakStr && (
                        <Typography variant="caption" color="text.secondary">
                            {t('pages.employees.insights.avgBreak')}: {averages.avgBreakStr}
                        </Typography>
                    )}
                    {averages.totalNetWorkStr && (
                        <Typography variant="caption" color="text.secondary">
                            {t('pages.employees.insights.totalHours')}: {averages.totalNetWorkStr}
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('pages.employees.insights.noAttendance')}
                </Typography>
            )}
        </Box>
    );
};

export default EmployeeCardInsights;
